import { useState } from "react";
import { Header } from "@/components/Header";
import { SEOHead } from "@/components/SEOHead";
import { useToast } from "@/hooks/use-toast";
import { Sparkles, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import { addonsList, propertiesList } from "@/data/addonsData";
import { AddOnsCheckoutDrawer } from "@/components/AddOnsCheckoutDrawer";

const bedroomFilters = [
  { id: "all", label: "All" },
  { id: "studio", label: "Studio" },
  { id: "1", label: "1 bedroom" },
  { id: "2", label: "2 bedrooms" },
  { id: "3+", label: "3+ bedrooms" },
];

const AddOns = () => {
  const { toast } = useToast();
  const [selectedAddon, setSelectedAddon] = useState<string | null>(null);
  const [selectedProperty, setSelectedProperty] = useState<string | null>(null);
  const [birthdayColor, setBirthdayColor] = useState("Chocolate");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [checkInDate, setCheckInDate] = useState<Date>();
  const [checkOutDate, setCheckOutDate] = useState<Date>();
  const [bedroomFilter, setBedroomFilter] = useState<string>("");

  const selectedAddonData = addonsList.find((a) => a.id === selectedAddon);
  const subtotal = selectedAddonData ? selectedAddonData.price : 0;
  const totalItems = selectedAddon ? 1 : 0;

  const toggleAddon = (id: string) => {
    setSelectedAddon((prev) => (prev === id ? null : id));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedAddonData) return;

    setIsSubmitting(true);
    const formData = new FormData(e.currentTarget);

    const requestedAddons = `${selectedAddonData.title}${selectedAddonData.hasColor ? ` (${birthdayColor})` : ""}`;
    const propertyName =
      propertiesList.find((p) => p.id === selectedProperty)?.name || "Unknown";

    const trackingPayload = {
      type: "external form_submission",
      timestamp: Date.now(),
      formId: "Add-Ons Request Form",
      formData: {
        first_name: formData.get("firstName"),
        last_name: formData.get("lastName"),
        email: formData.get("email"),
        phone: formData.get("phone"),
        "contact.checkin_date": formData.get("checkIn"),
        "contact.checkout_date": formData.get("checkOut"),
        "contact.property_name": propertyName,
        "contact.requested_addons": requestedAddons,
        "contact.addons_total": `$${subtotal.toFixed(2)}`,
      },
      formLabels: {
        first_name: "First Name",
        last_name: "Last Name",
        email: "Email Address",
        phone: "Phone Number",
        "contact.checkin_date": "Check-in Date",
        "contact.checkout_date": "Check-out Date",
        "contact.property_name": "Property",
        "contact.requested_addons": "Requested Add-ons",
        "contact.addons_total": "Add-ons Total",
      },
      url: window.location.href,
      title: document.title,
      path: window.location.pathname,
      userAgent: navigator.userAgent,
      trackingId: "tk_5a385db558864e73a9e6d4b36e7bcc1d",
      locationId: "7oQcAyw9kZpcBxPOtMLw",
      sessionId: crypto.randomUUID(),
    };

    try {
      await fetch(
        "https://backend.leadconnectorhq.com/external-tracking/events",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            version: "2021-07-28",
          },
          body: JSON.stringify(trackingPayload),
        },
      );

      await fetch(
        "https://services.leadconnectorhq.com/hooks/7oQcAyw9kZpcBxPOtMLw/webhook-trigger/b257a25a-bf74-4e1b-b39b-73606c0bd930",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...trackingPayload.formData,
            phone_number: formData.get("phone"),
            phoneNumber: formData.get("phone"),
            property_name: propertyName,
            contact_property_name: propertyName,
            contact_checkin_date: formData.get("checkIn"),
            contact_checkout_date: formData.get("checkOut"),
            "Where are you staying?": propertyName,
            requested_addons: requestedAddons,
            addons_total: `$${subtotal.toFixed(2)}`,
          }),
        },
      );
    } catch (e) {}

    if (selectedAddonData.link && selectedAddonData.link !== "#") {
      const email = formData.get("email") as string;
      const url = new URL(selectedAddonData.link);
      if (email) {
        url.searchParams.set("prefilled_email", email);
      }
      window.location.href = url.toString();
    } else {
      setIsSubmitting(false);
      setIsDrawerOpen(false);
      toast({
        title: "Stripe Link Missing",
        description:
          "Please update the Stripe link for this add-on in the code.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-background font-sans flex flex-col pb-24 md:pb-0">
      <SEOHead
        title="StayAwhile Add-Ons & Premium Upgrades"
        description="Enhance your stay with early check-in, late checkout, birthday surprises, and other premium upgrades from StayAwhile."
        canonical="https://bookstayawhile.com/addons"
      />
      <Header />

      <main className="flex-grow pt-16 md:pt-20">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8 px-4 relative">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] md:w-[80%] h-32 bg-primary/15 blur-[60px] -z-10 rounded-full pointer-events-none" />

            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-[10px] md:text-xs font-bold tracking-widest uppercase mb-4 shadow-[0_0_15px_rgba(255,236,0,0.1)]">
              <Sparkles className="w-3 h-3" />
              Premium Upgrades
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-3 tracking-tight bg-gradient-to-b from-foreground to-foreground/60 bg-clip-text text-transparent">
              Enhance Your Stay
            </h1>
            <p className="text-muted-foreground text-sm md:text-base max-w-md mx-auto">
              Select your property and elevate your experience with our curated
              collection of premium add-ons.
            </p>
          </div>

          {/* Property Selection */}
          <div className="mb-10">
            <div className="px-4 md:px-0 mb-4">
              <h2 className="text-xl font-bold">1. Where are you staying?</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Tap your unit photo to select it. Scroll down to see more.
              </p>
              <div className="mt-3 p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex gap-2 items-start">
                <span className="text-sm leading-none">⚠️</span>
                <p className="text-xs text-destructive font-semibold">
                  Please be sure to select the correct place, otherwise you may
                  pay for another guest's upgrade. All add-on purchases are
                  non-refundable.
                </p>
              </div>
            </div>

            {/* Bedroom filter pills */}
            <div className="px-4 md:px-0 mb-4">
              <div className="flex flex-wrap gap-2">
                {bedroomFilters.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setBedroomFilter(f.id)}
                    className={`px-4 py-2 rounded-full text-xs md:text-sm font-semibold transition-all duration-200 border ${
                      bedroomFilter === f.id
                        ? "bg-primary text-black border-primary shadow-[0_0_15px_rgba(255,236,0,0.2)]"
                        : "bg-card border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="px-4 md:px-0">
              <div className="grid grid-cols-3 md:grid-cols-4 gap-2 md:gap-4 max-h-[400px] md:max-h-[500px] overflow-y-auto pr-2 pb-4 scrollbar-thin scrollbar-thumb-primary/20 scrollbar-track-transparent">
                {propertiesList.map((prop) => {
                  const showTile =
                    bedroomFilter !== "" &&
                    (bedroomFilter === "all" ||
                      prop.bedrooms === bedroomFilter);

                  return (
                    <motion.div
                      key={prop.id}
                      data-bedrooms={prop.bedrooms}
                      onClick={() => setSelectedProperty(prop.id)}
                      whileTap={{ scale: 0.95 }}
                      className={`relative cursor-pointer overflow-hidden rounded-xl aspect-[4/5] md:aspect-square transition-all duration-300 flex flex-col ${
                        !showTile ? "hidden" : ""
                      } ${
                        selectedProperty === prop.id
                          ? "ring-2 ring-primary scale-[1.02] shadow-[0_0_15px_rgba(255,236,0,0.2)] bg-card"
                          : "ring-1 ring-border/50 bg-card hover:border-primary/50"
                      }`}
                    >
                      <div className="h-[60%] w-full relative bg-muted animate-pulse">
                        <img
                          src={prop.image}
                          alt={prop.name}
                          width={240}
                          height={240}
                          loading="lazy"
                          decoding="async"
                          onLoad={(e) =>
                            e.currentTarget.parentElement?.classList.remove(
                              "animate-pulse",
                              "bg-muted",
                            )
                          }
                          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300"
                        />
                        {selectedProperty === prop.id && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="absolute top-2 right-2 bg-primary text-black rounded-full p-0.5 shadow-lg z-10"
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </motion.div>
                        )}
                      </div>
                      <div className="p-2 md:p-3 flex flex-col justify-center flex-grow bg-card">
                        <p className="font-bold text-[11px] md:text-sm leading-tight text-foreground line-clamp-2">
                          {prop.name}
                        </p>
                        <p className="text-[10px] md:text-xs text-muted-foreground mt-0.5">
                          {prop.code}
                        </p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
              {bedroomFilter === "" && (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  Select a bedroom filter above to browse our properties.
                </div>
              )}
            </div>
          </div>

          {/* Add-Ons Selection */}
          <div className="mb-8 px-4 md:px-0">
            <h2 className="text-xl font-bold mb-4">2. Choose Add-Ons</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {addonsList.map((addon) => {
                const Icon = addon.icon;
                const isSelected = selectedAddon === addon.id;

                return (
                  <motion.div
                    key={addon.id}
                    onClick={() => toggleAddon(addon.id)}
                    whileTap={{ scale: 0.98 }}
                    className={`group cursor-pointer p-4 rounded-2xl border transition-all duration-300 ${
                      isSelected
                        ? "bg-primary/10 border-primary shadow-[0_0_15px_rgba(255,236,0,0.15)] scale-[1.02]"
                        : "bg-card border-border hover:border-primary/50"
                    }`}
                  >
                    <div className="flex gap-4 items-start">
                      <div
                        className={`p-3 rounded-xl flex-shrink-0 transition-colors duration-300 ${isSelected ? "bg-primary text-black shadow-lg" : "bg-muted text-muted-foreground"}`}
                      >
                        <Icon className="w-6 h-6" />
                      </div>
                      <div className="flex-grow">
                        <div className="flex justify-between items-start mb-1 gap-2">
                          <h3 className="font-bold text-lg leading-tight">
                            {addon.title}
                          </h3>
                          <div
                            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors duration-300 ${isSelected ? "border-primary bg-primary text-black" : "border-muted-foreground/30 group-hover:border-primary/50"}`}
                          >
                            {isSelected && <CheckCircle2 className="w-4 h-4" />}
                          </div>
                        </div>
                        {addon.badge && (
                          <div
                            className={`inline-block px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider mb-2 ${addon.badgeColor}`}
                          >
                            {addon.badge}
                          </div>
                        )}
                        <p className="text-sm text-muted-foreground mb-3">
                          {addon.desc}
                        </p>

                        {addon.hasColor && isSelected && (
                          <div
                            className="mb-3"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <select
                              value={birthdayColor}
                              onChange={(e) => setBirthdayColor(e.target.value)}
                              className="h-9 w-full rounded-md border border-primary/50 bg-background/80 px-3 py-1 text-sm outline-none focus:ring-1 focus:ring-primary text-foreground"
                            >
                              <option value="Chocolate">Chocolate Cake</option>
                              <option value="Vanilla">Vanilla Cake</option>
                              <option value="Red Velvet">
                                Red Velvet Cake
                              </option>
                              <option value="Strawberry">
                                Strawberry Cake
                              </option>
                            </select>
                          </div>
                        )}

                        <div className="flex items-center justify-between mt-2">
                          <span
                            className={`font-bold ${isSelected ? "text-primary" : "text-foreground"}`}
                          >
                            ${addon.price.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Policies Section */}
          <div className="mb-8 px-4 md:px-0">
            <h2 className="text-xl font-bold mb-4">3. Important Policies</h2>
            <div className="space-y-3">
              <div className="p-4 rounded-xl bg-card border border-white/5">
                <h3 className="font-bold text-sm mb-1 text-foreground">
                  Non-Refundable
                </h3>
                <p className="text-xs text-muted-foreground">
                  All add-on purchases are final and non-refundable. Please
                  double-check your dates and property selection before
                  completing your purchase.
                </p>
              </div>
              <div className="p-4 rounded-xl bg-card border border-white/5">
                <h3 className="font-bold text-sm mb-1 text-foreground">
                  Notice Requirements
                </h3>
                <p className="text-xs text-muted-foreground">
                  Early check-ins and late check-outs require at least 12 hours
                  notice to coordinate with our cleaning team. Birthday
                  surprises require 48 hours notice.
                </p>
              </div>
              <div className="p-4 rounded-xl bg-card border border-white/5">
                <h3 className="font-bold text-sm mb-1 text-foreground">
                  Forgotten Items Return
                </h3>
                <p className="text-xs text-muted-foreground">
                  The $25 service fee helps cover the time and care involved in
                  locating, packaging, and preparing your item for shipment.
                  Actual shipping charges are extra and based on the carrier's
                  rates.
                </p>
              </div>
            </div>
          </div>

          <div className="mb-24"></div>
        </div>
      </main>

      {/* Mobile-style Sticky Bottom Bar */}
      <div
        className={`fixed bottom-0 left-0 right-0 p-4 bg-background/80 backdrop-blur-xl border-t border-white/10 transform transition-transform duration-300 z-40 ${totalItems > 0 ? "translate-y-0" : "translate-y-full"}`}
      >
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <div className="flex-1">
            <p className="text-sm text-muted-foreground">
              Total ({totalItems} items)
            </p>
            <p className="text-2xl font-bold text-primary">
              ${subtotal.toFixed(2)}
            </p>
          </div>

          <AddOnsCheckoutDrawer
            isOpen={isDrawerOpen}
            onOpenChange={setIsDrawerOpen}
            selectedAddon={selectedAddonData}
            selectedPropertyId={selectedProperty}
            properties={propertiesList}
            subtotal={subtotal}
            birthdayColor={birthdayColor}
            setBirthdayColor={setBirthdayColor}
            isSubmitting={isSubmitting}
            onSubmit={handleSubmit}
            checkInDate={checkInDate}
            setCheckInDate={setCheckInDate}
            checkOutDate={checkOutDate}
            setCheckOutDate={setCheckOutDate}
          />
        </div>
      </div>
    </div>
  );
};

export default AddOns;
